import React, { useEffect, useState } from 'react';
import { vacationsAPI } from '../../api/api';
import Modal from '../Common/Modal';
import LoadingSpinner from '../Common/LoadingSpinner';
import { useLanguage } from '../../context/LanguageContext';
import { printVacationReport } from '../../utils/printVacationReport';

// One person's vacation statistics for a year: totals, how much of the
// allowance is used, days per month, and every entry with its day count,
// plus the overtime hours / day notes. Printable as a report.

const fmtDays = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));
const fmtHours = (n) => {
  const v = Math.round(Number(n || 0) * 100) / 100;
  return `${v > 0 ? '+' : ''}${v} h`;
};
const typeIcon = { vacation: '🏖️', sick: '🤒', other: '📌' };
const barColor = { vacation: 'bg-[#361826]', sick: 'bg-red-400', other: 'bg-gray-400' };

const keyToDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const VacationUserStats = ({ userId, initialYear, onClose }) => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setYear(initialYear);
  }, [initialYear, userId]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    vacationsAPI
      .getUserStats(userId, year)
      .then((res) => setData(res.data.data))
      .catch((err) => alert(t('vacations.failedToLoad', { error: err.response?.data?.error || err.message })))
      .finally(() => setLoading(false));
  }, [userId, year, t]);

  const s = data?.summary;
  // Available this year = allowance + days carried over from earlier years.
  const available = s ? s.allowance + (s.carryover || 0) : 0;
  const usedPct = available > 0 ? Math.min(100, (s.taken / available) * 100) : 0;
  const plannedPct = available > 0 ? Math.min(100 - usedPct, (s.planned / available) * 100) : 0;
  const maxMonth = data ? Math.max(1, ...data.months.map((m) => m.vacation + m.sick + m.other)) : 1;
  const today = todayKey();

  const entryState = (e) => {
    if (e.end_date < today) return t('vacations.statePast');
    if (e.start_date > today) return t('vacations.stateUpcoming');
    return t('vacations.stateNow');
  };

  const fmtDate = (key) => keyToDate(key).toLocaleDateString(locale);
  const typeLabel = (e) => `${t(`vacations.type_${e.type}`)}${e.half_day ? ` (${t('vacations.halfDay')})` : ''}`;

  const print = () =>
    printVacationReport({
      title: `${t('vacations.reportTitle', { year })} – ${s.user_name}`,
      totals: [
        [t('vacations.colAllowance'), fmtDays(s.allowance)],
        [t('vacations.colCarryover'), fmtDays(s.carryover || 0)],
        [t('vacations.colTaken'), fmtDays(s.taken)],
        [t('vacations.colPlanned'), fmtDays(s.planned)],
        [t('vacations.reportRemaining'), fmtDays(s.remaining), true],
        [t('vacations.colSick'), fmtDays(s.sick)],
        [t('vacations.colOther'), fmtDays(s.other)],
        [t('vacations.overtimeYear'), fmtHours(s.overtime_hours)],
        [t('vacations.overtimeBalance'), fmtHours(s.overtime_balance), true],
      ],
      months: {
        headers: [
          '',
          t('vacations.type_vacation'),
          t('vacations.type_sick'),
          t('vacations.type_other'),
          t('vacations.colOvertime'),
        ],
        rows: data.months.map((m) => [
          new Date(year, m.month - 1, 1).toLocaleDateString(locale, { month: 'long' }),
          fmtDays(m.vacation),
          fmtDays(m.sick),
          fmtDays(m.other),
          m.overtime ? fmtHours(m.overtime) : '',
        ]),
      },
      entries: {
        headers: [t('vacations.from'), t('vacations.to'), t('vacations.type'), t('vacations.days'), t('vacations.note'), ''],
        rows: data.entries.map((e) => [
          fmtDate(e.start_date),
          fmtDate(e.end_date),
          typeLabel(e),
          fmtDays(e.days),
          e.note,
          entryState(e),
        ]),
      },
      overtime: {
        headers: [t('vacations.date'), t('vacations.hours'), t('vacations.note')],
        rows: (data.overtime || []).map((o) => [fmtDate(o.date), o.hours ? fmtHours(o.hours) : '', o.note]),
      },
      labels: {
        perMonth: t('vacations.perMonth'),
        entries: t('vacations.entriesTitle'),
        overtime: t('vacations.overtimeTitle'),
        none: '-',
      },
    });

  return (
    <Modal
      isOpen={!!userId}
      onClose={onClose}
      title={`📊 ${s?.user_name || ''}`}
      size='lg'
    >
      <div className='flex items-center justify-center gap-3 mb-4'>
        <button onClick={() => setYear(year - 1)} className='btn-secondary px-3'>
          ‹
        </button>
        <span className='text-lg font-semibold text-gray-800'>{year}</span>
        <button onClick={() => setYear(year + 1)} className='btn-secondary px-3'>
          ›
        </button>
        <button onClick={print} className='btn-secondary ml-4' disabled={loading || !data}>
          🖨️ {t('vacations.print')}
        </button>
      </div>

      {loading || !data ? (
        <LoadingSpinner />
      ) : (
        <div className='space-y-6'>
          <div className='grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3'>
            {[
              ['colAllowance', s.allowance, 'text-gray-900'],
              ['colCarryover', s.carryover || 0, (s.carryover || 0) < 0 ? 'text-red-600' : 'text-gray-900'],
              ['colTaken', s.taken, 'text-[#361826]'],
              ['colPlanned', s.planned, 'text-blue-600'],
              ['colRemaining', s.remaining, s.remaining < 0 ? 'text-red-600' : 'text-green-700'],
              ['colSick', s.sick, 'text-red-500'],
              ['colOther', s.other, 'text-gray-600'],
            ].map(([label, value, color]) => (
              <div key={label} className='rounded-lg border border-gray-200 p-3 text-center'>
                <p className='text-xs text-gray-500'>{t(`vacations.${label}`)}</p>
                <p className={`text-2xl font-bold ${color}`}>{fmtDays(value)}</p>
              </div>
            ))}
            <div
              className='rounded-lg border border-gray-200 p-3 text-center'
              title={t('vacations.overtimeThisYear', { hours: fmtHours(s.overtime_hours) })}
            >
              <p className='text-xs text-gray-500'>{t('vacations.colOvertime')}</p>
              <p className={`text-2xl font-bold ${s.overtime_balance < 0 ? 'text-red-600' : 'text-amber-700'}`}>
                {fmtHours(s.overtime_balance)}
              </p>
            </div>
          </div>

          <div>
            <p className='text-sm text-gray-600 mb-1'>
              {t('vacations.allowanceUsed', {
                used: fmtDays(s.taken + s.planned),
                total: fmtDays(available),
              })}
            </p>
            <div className='h-3 w-full rounded-full bg-gray-100 overflow-hidden flex'>
              <div className='bg-[#361826]' style={{ width: `${usedPct}%` }} title={t('vacations.colTaken')} />
              <div className='bg-blue-400' style={{ width: `${plannedPct}%` }} title={t('vacations.colPlanned')} />
            </div>
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-2'>{t('vacations.perMonth')}</p>
            <div className='grid grid-cols-12 gap-1 items-end h-32'>
              {data.months.map((m) => {
                const total = m.vacation + m.sick + m.other;
                return (
                  <div key={m.month} className='flex flex-col items-center justify-end h-full'>
                    {total > 0 && <span className='text-[10px] text-gray-600 mb-0.5'>{fmtDays(total)}</span>}
                    <div
                      className='w-full max-w-[28px] flex flex-col-reverse rounded-t overflow-hidden'
                      style={{ height: `${(total / maxMonth) * 100}%` }}
                      title={`${t('vacations.type_vacation')}: ${fmtDays(m.vacation)}, ${t('vacations.type_sick')}: ${fmtDays(m.sick)}, ${t('vacations.type_other')}: ${fmtDays(m.other)}`}
                    >
                      {['vacation', 'sick', 'other'].map((type) =>
                        m[type] > 0 ? (
                          <div key={type} className={barColor[type]} style={{ flexGrow: m[type] }} />
                        ) : null,
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='grid grid-cols-12 gap-1 mt-1'>
              {data.months.map((m) => (
                <span key={m.month} className='text-[10px] text-gray-500 text-center capitalize'>
                  {new Date(year, m.month - 1, 1).toLocaleDateString(locale, { month: 'short' })}
                </span>
              ))}
            </div>
            <div className='flex gap-4 mt-2 text-xs text-gray-600'>
              {['vacation', 'sick', 'other'].map((type) => (
                <span key={type} className='flex items-center gap-1'>
                  <span className={`w-2.5 h-2.5 rounded-sm ${barColor[type]}`} />
                  {t(`vacations.type_${type}`)}
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-2'>{t('vacations.entriesTitle')}</p>
            {data.entries.length === 0 ? (
              <p className='text-sm text-gray-500'>{t('vacations.noEntries')}</p>
            ) : (
              <table className='min-w-full divide-y divide-gray-200 text-sm'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.from')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.to')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.type')}</th>
                    <th className='px-3 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.days')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.note')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500' />
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {data.entries.map((e) => (
                    <tr key={e.id}>
                      <td className='px-3 py-2 whitespace-nowrap'>{keyToDate(e.start_date).toLocaleDateString(locale)}</td>
                      <td className='px-3 py-2 whitespace-nowrap'>{keyToDate(e.end_date).toLocaleDateString(locale)}</td>
                      <td className='px-3 py-2 whitespace-nowrap'>
                        {typeIcon[e.type]} {t(`vacations.type_${e.type}`)}
                        {e.half_day ? ` (${t('vacations.halfDay')})` : ''}
                      </td>
                      <td className='px-3 py-2 text-center font-semibold'>{fmtDays(e.days)}</td>
                      <td className='px-3 py-2 text-gray-600'>{e.note || '-'}</td>
                      <td className='px-3 py-2 text-xs text-gray-500 whitespace-nowrap'>{entryState(e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-2'>
              {t('vacations.overtimeTitle')}{' '}
              <span className='font-normal text-gray-500'>
                ({t('vacations.overtimeThisYear', { hours: fmtHours(s.overtime_hours) })})
              </span>
            </p>
            {(data.overtime || []).length === 0 ? (
              <p className='text-sm text-gray-500'>{t('vacations.noOvertime')}</p>
            ) : (
              <table className='min-w-full divide-y divide-gray-200 text-sm'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.date')}</th>
                    <th className='px-3 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.hours')}</th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.note')}</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {data.overtime.map((o) => (
                    <tr key={o.id}>
                      <td className='px-3 py-2 whitespace-nowrap'>{fmtDate(o.date)}</td>
                      <td
                        className={`px-3 py-2 text-center font-semibold whitespace-nowrap ${
                          o.hours < 0 ? 'text-red-600' : ''
                        }`}
                      >
                        {o.hours ? fmtHours(o.hours) : '📝'}
                      </td>
                      <td className='px-3 py-2 text-gray-600'>{o.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VacationUserStats;
